import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate, useSearchParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Phone, ShoppingCart, Wrench, Shield, LogOut, Bell, Package, Briefcase, FolderKanban, Settings, User, Code } from "lucide-react";
import { createContext, useContext, useState, useEffect } from "react";
import { ThemeProvider } from "@/context/ThemeContext";
import MaintenancePage from "@/pages/MaintenancePage";
import OrderRequestPage from "@/pages/OrderRequestPage";
import MicrobiologyTestingPage from "@/pages/MicrobiologyTestingPage";
import BioburdenServicesPage from "@/pages/BioburdenServicesPage";
import DITankExchangeCatalogPage from "@/pages/DITankExchangeCatalogPage";
import InvestorPortalPage from "@/pages/InvestorPortalPage";
import InvestorSharePage from "@/pages/InvestorSharePage";
import FrostBankCoverLetterPage from "@/pages/FrostBankCoverLetterPage";
import LivingWatersProposalPage from "@/pages/LivingWatersProposalPage";
import LivingWatersLandingPage from "@/pages/LivingWatersLandingPage";
import ProposalsIndexPage from "@/pages/ProposalsIndexPage";
import FinancialProjectionsPage from "@/pages/FinancialProjectionsPage";
import HospitalPresentationPage from "@/pages/HospitalPresentationPage";
import PitchDeckPage from "@/pages/PitchDeckPage";
import LoginPage from "@/pages/LoginPage";
import HelpPage from "@/pages/HelpPage";
import AIHelpPage from "@/pages/AIHelpPage";
import HealthSystemRegisterPage from "@/pages/HealthSystemRegisterPage";
import HospitalRegisterSuccessPage from "@/pages/HospitalRegisterSuccessPage";
import HospitalLoginPage from "@/pages/HospitalLoginPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminDashboardV2 from "@/pages/AdminDashboardV2";
import SalesRepDashboard from "@/pages/SalesRepDashboard";
import ContractorPortalPage from "./pages/ContractorPortalPage";
import WorkerDashboard from "@/pages/WorkerDashboard";
import ProjectManagementPage from "@/pages/ProjectManagementPage";
import ContractorRegisterPage from "@/pages/ContractorRegisterPage";
import TeamJoinPage from "@/pages/TeamJoinPage";
import TeamApprovalsPage from "@/pages/admin/TeamApprovalsPage";
import AAMIST108Page from "@/pages/AAMIST108Page";
import ST108GuidePage from "@/pages/ST108GuidePage";
import HospitalBackorderPage from "@/pages/HospitalBackorderPage";
import PublicBackorderQuotePage from "@/pages/PublicBackorderQuotePage";
import WorkOrderSignOffPage from "@/pages/WorkOrderSignOffPage";
import IncidentDetailPage from "@/pages/IncidentDetailPage";
import ContractorJobPortal from "@/pages/ContractorJobPortal";
import ContractorLoginPage from "@/pages/ContractorLoginPage";
import ContractorDashboardPage from "@/pages/ContractorDashboardPage";
import ContractorDashboardV2 from "@/pages/ContractorDashboardV2";
import ContractorProfilePage from "@/pages/ContractorProfilePage";
import ContractorRecruitmentPage from "@/pages/ContractorRecruitmentPage";
import ContractorRecruitmentPageV2 from "@/pages/ContractorRecruitmentPageV2";
import SupplierRecruitmentPage from "@/pages/SupplierRecruitmentPage";
import JoinNetworkPage from "@/pages/JoinNetworkPage";
import WordPressRedirect from "@/components/WordPressRedirect";
import { getWordpressRedirect } from "@/data/wordpressRedirects";
import ContractorRequestAccessPage from "@/pages/ContractorRequestAccessPage";
import SupplierRequestAccessPage from "@/pages/SupplierRequestAccessPage";
import AskIsaacDashboard from "@/pages/AskIsaacDashboard";
import SupplierQuotePortal from "@/pages/SupplierQuotePortal";
import ContractorMaintenanceQuotePortal from "@/pages/ContractorMaintenanceQuotePortal";
import HospitalDashboard from "@/pages/HospitalDashboard";
import HospitalDashboardPortal from "@/pages/HospitalDashboardPortal";
import HospitalCapabilitiesPage from "@/pages/HospitalCapabilitiesPage";
import MarketplacePage from "@/pages/MarketplacePage";
import CustomerOrderPortal from "@/pages/CustomerOrderPortal";
import HospitalMarketplace from "@/pages/HospitalMarketplace";
import FilmopStorePage from "@/pages/FilmopStorePage";
import NetworkDashboardPage from "@/pages/NetworkDashboardPage";
import HospitalSetupPage from "@/pages/HospitalSetupPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import BackorderSourcingPage from "@/pages/BackorderSourcingPage";
import BackorderLiveInventoryPage from "@/pages/BackorderLiveInventoryPage";
import SolutionsOverviewPage from "@/pages/SolutionsOverviewPage";
import DiversityLandingPage from "@/pages/DiversityLandingPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import EmailPreferencesPage from "@/pages/EmailPreferencesPage";
import MedicalDevicesLandingPage from "@/pages/MedicalDevicesLandingPage";
import ViveHealthShopPage from "@/pages/ViveHealthShopPage";
import SubBidPage from "@/pages/SubBidPage";
import PermitSignPage from "@/pages/PermitSignPage";
import ComplaintFormPage from "@/pages/ComplaintFormPage";
import IMSHARTProductPage from "@/pages/IMSHARTProductPage";
import GeneralContractingPage from "@/pages/solutions/GeneralContractingPage";
import WaterTreatmentPage from "@/pages/solutions/WaterTreatmentPage";
import DiabetesManagementPage from "@/pages/solutions/DiabetesManagementPage";
import RebateProgramPage from "@/pages/solutions/RebateProgramPage";
import RebateProgramDeskPage from "@/pages/admin/RebateProgramDeskPage";
import SoftwareAppPage from "@/pages/solutions/SoftwareAppPage";
import HospitalRebatesPage from "@/pages/HospitalRebatesPage";
import AmericanSurgicalPage from "@/pages/AmericanSurgicalPage";
import AmericanSurgicalHospitalDemoPage from "@/pages/AmericanSurgicalHospitalDemoPage";
import WelcomeLetterPage from "@/pages/WelcomeLetterPage";
import SupplierWelcomeLetterPage from "@/pages/SupplierWelcomeLetterPage";
import ContractorWelcomeLetterPage from "@/pages/ContractorWelcomeLetterPage";
import ContractorSetupPage from "@/pages/ContractorSetupPage";
import ContractorAgreementPage from "@/pages/ContractorAgreementPage";
import ContractorCompliancePage from "@/pages/ContractorCompliancePage";
import ContractorQuotePage from "@/pages/ContractorQuotePage";
import HospitalQuoteApprovalPage from "@/pages/HospitalQuoteApprovalPage";
import { SupplierLoginPage, SupplierSetupPage, SupplierDashboard } from "@/pages/SupplierPortal";
import SupplierDashboardV2 from "@/pages/SupplierDashboardV2";
import LandingPage from "@/pages/LandingPage";
import AssetLookupPage from "@/pages/AssetLookupPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SalesOnePagerPage from "@/pages/SalesOnePagerPage";
import HospitalExecutiveLetterPage from "@/pages/HospitalExecutiveLetterPage";
import HospitalCommandCenter from "@/pages/HospitalCommandCenter";
import UserGuidesPage from "@/pages/UserGuidesPage";
import TelegramWalkthroughPage from "@/pages/TelegramWalkthroughPage";
import AccountProfilePage from "@/pages/AccountProfilePage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import QuickBidPage from "@/pages/QuickBidPage";
import VendorShowcasePage from "@/pages/VendorShowcasePage";
import FleetDashboardPage from "@/pages/FleetDashboardPage";
import HospitalTrackingPage from "@/pages/HospitalTrackingPage";
import SalesPlaybookPage from "@/pages/SalesPlaybookPage";
import NeuroSpongeDistribution from "@/pages/NeuroSpongeDistribution";
import ContractorPublicProfile from "@/pages/ContractorPublicProfile";
import SupplierPublicProfile from "@/pages/SupplierPublicProfile";
import SurgicalCatalog from "@/pages/SurgicalCatalog";
import SolMCategoryPage from "@/pages/SolMCategoryPage";
import BearEntCategoryPage from "@/pages/BearEntCategoryPage";
import { QuoteCartProvider } from "@/contexts/QuoteCartContext";
import QuoteCartDialog from "@/components/QuoteCartDialog";
import AskIsaacMovedBanner from "@/components/AskIsaacMovedBanner";
import SupplyChainPlaybook from "@/pages/SupplyChainPlaybook";
import OnboardingPlaybookPage from "@/pages/OnboardingPlaybookPage";
import DeveloperHubPage from "@/pages/DeveloperHubPage";
import InvestorOnePagerPage from "@/pages/InvestorOnePagerPage";
import PublicRegisterPage from "@/pages/PublicRegisterPage";
import SelfRegisterPage from "@/pages/SelfRegisterPage";
import HospitalSelfRegisterPage from "@/pages/HospitalSelfRegisterPage";
import HospitalSetupPasswordPage from "@/pages/HospitalSetupPasswordPage";
import AboutPage from "@/pages/AboutPage";
// AskIsaac + GeorgeCV pages are intentionally not routed for now
// (kept in the codebase for future revival).
import HealthWrightCoverLetterPage from "@/pages/HealthWrightCoverLetterPage";
import ComplianceDemoSuite from "@/components/demo/ComplianceDemoSuite";
import SecurityWhitepaper from "@/pages/SecurityWhitepaper";
import EVSDemoPage from "@/pages/EVSDemoPage";
import WaterPurificationDemoPage from "@/pages/WaterPurificationDemoPage";
import DemoGalleryPage from "@/pages/DemoGalleryPage";
import DownloadsPage from "@/pages/DownloadsPage";
import OnboardingModal from "@/components/OnboardingModal";
import BottomNavigation from "@/components/BottomNavigation";
import Footer from "@/components/Footer";
import IsaacChat from "@/components/IsaacChat";
import PhilanthropyPage from "@/pages/admin/PhilanthropyPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import MarketplaceProductPage from "@/pages/MarketplaceProductPage";
import ContactPage from "@/pages/ContactPage";
import AvagardLandingPage from "@/pages/AvagardLandingPage";
import BlueFluxLandingPage from "@/pages/BlueFluxLandingPage";
import BlueFluxAboutPage from "@/pages/BlueFluxAboutPage";
import PortOfHoustonDemo from "@/pages/PortOfHoustonDemo";
import GreenKleenPage from "@/pages/GreenKleenPage";
import GreenKleenVacuumBagsPage from "@/pages/GreenKleenVacuumBagsPage";
import GlovesPage from "@/pages/GlovesPage";
import AnsellGlovesPage from "@/pages/AnsellGlovesPage";
import FurniturePage from "@/pages/FurniturePage";
import JanitorialCatalogPage from "@/pages/JanitorialCatalogPage";
import JanitorialCategoryPage from "@/pages/JanitorialCategoryPage";
import JanitorialProductPage from "@/pages/JanitorialProductPage";
import LogoLabPage from "@/pages/LogoLabPage";
import AamiSt108Page from "@/pages/AamiSt108Page";
import AamiTir34Page from "@/pages/AamiTir34Page";
import AamiSt91Page from "@/pages/AamiSt91Page";
import JointCommissionWaterPage from "@/pages/JointCommissionWaterPage";
import HemodialysisWaterPage from "@/pages/HemodialysisWaterPage";
import DialysisCommandCenterPage from "@/pages/DialysisCommandCenterPage";
import SterileProcessingCommandCenterPage from "@/pages/SterileProcessingCommandCenterPage";
import SearchResultsPage from "@/pages/SiteSearch";
import WhatAreNeuroPattiesPage from "@/pages/WhatAreNeuroPattiesPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Sterile Mate Logo URLs
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";
const ASKISAAC_LOGO_DARK = "/logos/askisaac-b-monogram.svg";
// Legacy Sterile Mate Logo (kept for reference)
const STERILE_MATE_LOGO = "/logos/askisaac-b-monogram.svg";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("sterilemate_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else {
            localStorage.removeItem("sterilemate_token");
            setToken(null);
          }
        } catch (error) {
          console.error("Auth check failed:", error);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = (newToken, userData) => {
    localStorage.setItem("sterilemate_token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("sterilemate_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    try { sessionStorage.setItem("redirect_after_login", location.pathname + location.search); }
    catch (_) { /* ignore */ }
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};
// Redirect helper for legacy email links — preserves hash anchors
// (react-router <Navigate to="/#quick-quote"> strips the hash, so we
// do a full browser replace which lets LandingPage scroll to #quick-quote).
const QuoteCartLegacyRedirect = () => {
  useEffect(() => {
    window.location.replace("/#quick-quote");
  }, []);
  return null;
};


// Widget Header Component
const WidgetHeader = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  
  useEffect(() => {
    const fetchNotifications = async () => {
      if (user) {
        try {
          const token = localStorage.getItem("sterilemate_token");
          const response = await fetch(`${API}/notifications`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setNotifications(data.notifications || []);
          }
        } catch (error) {
          console.error("Failed to fetch notifications:", error);
        }
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);
  
  const unreadCount = notifications.filter(n => !n.read).length;
  
  return (
    <header className="flex items-center justify-between p-4 border-b border-slate-200/50 bg-white/80 backdrop-blur-lg sticky top-0 z-50 shadow-sm animate-slide-down">
      <Link to="/" className="flex items-center gap-3 group">
        <div className="relative">
          <img 
            src={STERILE_MATE_LOGO} 
            alt="Sterile Mate" 
            className="h-12 w-auto object-contain transition-transform group-hover:scale-105"
          />
        </div>
      </Link>
      
      <nav className="flex items-center gap-2">
        <Link 
          to="/maintenance"
          data-testid="nav-maintenance"
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
            location.pathname === '/maintenance' 
              ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/25' 
              : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
          }`}
        >
          <Wrench className="w-4 h-4" />
          <span className="hidden sm:inline">Maintenance</span>
        </Link>
        <Link 
          to="/order-request"
          data-testid="nav-order-request"
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
            location.pathname === '/order-request' 
              ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/25' 
              : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
          }`}
        >
          <ShoppingCart className="w-4 h-4" />
          <span className="hidden sm:inline">Orders</span>
        </Link>
        
        {user ? (
          <>
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-300"
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-red-500 to-rose-500 text-white text-xs rounded-full flex items-center justify-center font-bold shadow-lg animate-bounce-subtle">
                    {unreadCount}
                  </span>
                )}
              </button>
              
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 max-h-96 overflow-y-auto animate-scale-in">
                  <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                    <span className="font-semibold text-slate-800">Notifications</span>
                    {unreadCount > 0 && (
                      <button 
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        onClick={async () => {
                          const token = localStorage.getItem("sterilemate_token");
                          await fetch(`${API}/notifications/read-all`, {
                            method: 'PUT',
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setNotifications(notifications.map(n => ({ ...n, read: true })));
                        }}
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-slate-400 text-sm">
                      No notifications
                    </div>
                  ) : (
                    notifications.slice(0, 10).map((notif) => (
                      <div
                        key={notif.id}
                        className={`p-4 border-b border-slate-50 last:border-0 transition-colors ${!notif.read ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}
                      >
                        <p className="text-sm text-slate-800 font-medium">{notif.title}</p>
                        <p className="text-xs text-slate-500 mt-1">{notif.message}</p>
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(notif.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            
            {/* Admin/Worker Dashboard Link */}
            {(user.role === 'super_admin' || user.role === 'hub_admin' || user.role === 'project_manager') && (
              <Link 
                to="/admin"
                data-testid="nav-admin"
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  location.pathname.startsWith('/admin') 
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-500/25' 
                    : 'text-slate-600 hover:text-emerald-600 hover:bg-emerald-50'
                }`}
              >
                <Shield className="w-4 h-4" />
                <span className="hidden sm:inline">Admin</span>
              </Link>
            )}
            
            {/* Developer Hub - visible to all logged in users */}
            <Link 
              to="/developer-hub"
              data-testid="nav-developer"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                location.pathname === '/developer-hub' 
                  ? 'bg-gradient-to-r from-indigo-600 to-violet-500 text-white shadow-lg shadow-indigo-500/25' 
                  : 'text-slate-600 hover:text-indigo-600 hover:bg-indigo-50'
              }`}
            >
              <Code className="w-4 h-4" />
              <span className="hidden sm:inline">Developers</span>
            </Link>
            
            {user.role === 'maintenance_worker' && (
              <Link 
                to="/worker"
                data-testid="nav-worker"
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  location.pathname.startsWith('/worker') 
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-500/25' 
                    : 'text-slate-600 hover:text-emerald-600 hover:bg-emerald-50'
                }`}
              >
                <Wrench className="w-4 h-4" />
                <span className="hidden sm:inline">My Work</span>
              </Link>
            )}
            
            {/* Settings Gear Icon */}
            <Link 
              to="/account/settings"
              data-testid="nav-settings"
              className="p-2.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-300"
              title="Platform & Privacy Settings"
            >
              <Settings className="w-5 h-5" />
            </Link>

            {/* Avatar Dropdown */}
            <div className="relative group">
              <Link 
                to="/account/profile"
                className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-slate-100 transition-all"
                data-testid="nav-profile"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white text-sm font-bold overflow-hidden">
                  {user.profile_image ? (
                    <img src={user.profile_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || "U"
                  )}
                </div>
              </Link>
              {/* Dropdown on hover */}
              <div className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="p-3 border-b border-slate-100">
                  <p className="font-semibold text-slate-800 truncate">{user.name || "User"}</p>
                  <p className="text-xs text-slate-500 truncate">{user.email}</p>
                </div>
                <div className="p-1">
                  <Link to="/account/profile" className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">
                    <User className="w-4 h-4" />
                    My Profile
                  </Link>
                  <Link to="/account/settings" className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                </div>
                <div className="p-1 border-t border-slate-100">
                  <button
                    onClick={logout}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg w-full text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
            
            <button
              onClick={logout}
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 transition-all duration-300 sm:hidden"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </>
        ) : (
          <Link 
            to="/login"
            data-testid="nav-login"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 transition-all duration-300"
          >
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Staff Login</span>
          </Link>
        )}
      </nav>
    </header>
  );
};

// Widget Footer Component
const WidgetFooter = () => (
  <footer className="border-t border-slate-200/50 bg-white/80 backdrop-blur-lg p-4 mt-auto">
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
      <div className="flex items-center gap-2 text-slate-600">
        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
          <Phone className="w-4 h-4 text-white" />
        </div>
        <span>Support: </span>
        <a 
          href="tel:800-988-3892" 
          className="text-blue-600 hover:text-blue-700 font-semibold transition-colors"
          data-testid="support-phone"
        >
          800.988.3892
        </a>
      </div>
      <div className="flex items-center gap-4 text-slate-500 text-xs font-medium">
        <span className="px-3 py-1 bg-slate-100 rounded-full">Ship Via: Best Way</span>
        <span className="px-3 py-1 bg-slate-100 rounded-full">Terms: Net 30</span>
        <Link
          to="/medical-device-complaint-form"
          data-testid="widget-footer-complaint-form-link"
          className="px-3 py-1 bg-amber-50 text-amber-800 rounded-full hover:bg-amber-100 font-semibold transition-colors"
        >
          Medical Device Complaint Form
        </Link>
      </div>
    </div>
  </footer>
);

// Home component
const HomeRedirect = () => {
  return (
    <div className="relative flex flex-col items-center justify-center min-h-[70vh] p-8 text-center overflow-hidden">
      {/* Animated background blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-cyan-400/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '0s' }} />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-gradient-to-br from-cyan-400/15 to-blue-400/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-gradient-to-br from-blue-500/10 to-indigo-400/10 rounded-full blur-3xl animate-wave" />
      </div>
      
      <div className="relative z-10">
        {/* Logo with glow effect */}
        <div className="relative mb-10 animate-fade-in-up">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-400/30 to-cyan-400/30 rounded-full blur-2xl scale-150 animate-pulse-glow" />
          <img 
            src={STERILE_MATE_LOGO} 
            alt="Sterile Mate" 
            className="relative h-28 w-auto object-contain mx-auto animate-float drop-shadow-2xl"
          />
        </div>
        
        {/* Headline with gradient */}
        <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-800 mb-5 animate-fade-in-up stagger-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Welcome to <span className="gradient-text">Sterile Mate</span>
        </h2>
        
        {/* Subtitle with animation */}
        <p className="text-slate-500 mb-4 max-w-xl text-lg leading-relaxed animate-fade-in-up stagger-2">
          Healthcare water purification and sterile processing solutions.
        </p>
        <p className="text-blue-600 font-semibold mb-12 text-lg animate-fade-in-up stagger-3">
          Request maintenance support or order supplies below.
        </p>
        
        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-5 justify-center animate-fade-in-up stagger-4">
          <Link 
            to="/maintenance"
            data-testid="home-maintenance-btn"
            className="btn-primary flex items-center justify-center gap-3 text-lg group"
          >
            <Wrench className="w-5 h-5 group-hover:rotate-12 transition-transform" />
            Report Maintenance Issue
          </Link>
          <Link 
            to="/order-request"
            data-testid="home-order-btn"
            className="btn-secondary flex items-center justify-center gap-3 text-lg group"
          >
            <Package className="w-5 h-5 group-hover:scale-110 transition-transform" />
            Request Quote / Backorder
          </Link>
        </div>

        {/* Partner Login Button */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up stagger-5">
          <Link
            to="/login"
            data-testid="home-partner-login-btn"
            className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-700 hover:to-slate-600 text-white rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] group"
          >
            <FolderKanban className="w-5 h-5 group-hover:scale-110 transition-transform" />
            <span className="font-semibold">Project Management Partners Login</span>
          </Link>
          <Link
            to="/hospital/backorders"
            data-testid="home-hospital-backorder-btn"
            className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] group"
          >
            <Package className="w-5 h-5 group-hover:scale-110 transition-transform" />
            <span className="font-semibold">Hospital Backorder Portal</span>
          </Link>
        </div>
        
        {/* Trust badges */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-8 animate-fade-in-up stagger-6">
          <div className="flex items-center gap-2 text-slate-400">
            <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="text-sm font-medium text-slate-600">FDA Compliant</span>
          </div>
          <Link 
            to="/st108"
            data-testid="home-st108-link"
            className="flex items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors group cursor-pointer"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center group-hover:shadow-lg group-hover:scale-105 transition-all">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <span className="text-sm font-medium text-slate-600 group-hover:text-blue-600">AAMI ST108</span>
          </Link>
          <div className="flex items-center gap-2 text-slate-400">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span className="text-sm font-medium text-slate-600">24/7 Support</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Full-width wrapper component for pages that need to break out of widget-container
const FullWidthRoute = ({ children }) => (
  <div className="fixed inset-0 z-40 overflow-auto">
    {children}
  </div>
);

// /register dispatcher:
//   ?type=contractor / ?type=supplier  → full self-service profile + doc upload form
//   default (hospital / no type)       → legacy email magic-link request form
const RegisterDispatcher = () => {
  const [params] = useSearchParams();
  const t = (params.get("type") || "").toLowerCase();
  if (t === "contractor" || t === "supplier") return <SelfRegisterPage />;
  return <PublicRegisterPage />;
};

function AppContent() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  // Deep links into a protected area must not redirect while the session is
  // still being checked — that is what used to bounce /admin/... to /admin.
  const gate = (element) => {
    if (authLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="spinner" />
        </div>
      );
    }
    if (!user) {
      try { sessionStorage.setItem("redirect_after_login", location.pathname + location.search); }
      catch (_) { /* ignore */ }
      return <Navigate to="/login" replace />;
    }
    return element;
  };

  // Pages where the Isaac floating chat bubble should be hidden — currently
  // suppressed inside the Admin Command Center and Hospital staff portal
  // because it overlaps primary CTAs in the lower-right corner.
  const hideIsaacChat =
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/hospital/portal');
  
  // Define pages where onboarding should NEVER show
  const pagesNoOnboarding = [
    '/supplier/quote/',
    '/supplier/setup/',
    '/supplier/login',
    '/contractor/register',
    '/contractor/login',
    '/contractor/setup/',
    '/contractor/dashboard',
    '/contractor/job/',
    '/login',
    '/admin',
    '/how-it-works',
    '/help',
    '/st108',
    '/investor',
    '/pitch-deck',
    '/demo/',
    '/demos',
    '/hospital/',
    '/maintenance',
    '/order-request',
    '/asset/',
    '/account/',
    '/permit/',
    '/philanthropy'
  ];
  
  // Check if current route is a page where onboarding should be disabled
  const isNoOnboardingPage = pagesNoOnboarding.some(path => 
    location.pathname.startsWith(path)
  );
  
  // Check if current route is a full-width page
  const isFullWidthPage = getWordpressRedirect(location.pathname) !== null ||
    location.pathname === '/' || 
    location.pathname === '/philanthropy' ||
    location.pathname === '/team/join' ||
    location.pathname === '/about' ||
    location.pathname === '/cover-letter/healthwright-technologies' ||
    location.pathname === '/proposals' ||
    location.pathname.startsWith('/proposals/') ||
    location.pathname === '/living-waters' ||
    location.pathname === '/share/living-waters' ||
    location.pathname === '/ai-assistant' ||
    location.pathname === '/investor' || 
    location.pathname === '/investor/one-pager' || 
    location.pathname === '/investor/financials' ||
    location.pathname === '/investor/frost-bank' ||
    location.pathname === '/investor/frost-bank/letter' ||
    location.pathname === '/pitch-deck' ||
    location.pathname === '/demo/evs' ||
    location.pathname === '/demo/water-purification' ||
    location.pathname === '/demo/port-of-houston' ||
    location.pathname === '/demos' ||
    location.pathname === '/hospital-presentation' || 
    location.pathname === '/contractor/register' || 
    location.pathname === '/st108' || 
    location.pathname === '/st108/guide' || 
    location.pathname === '/aami-st108' || 
    location.pathname === '/hospital/backorders' ||
    location.pathname === '/hospital/dashboard' ||
    location.pathname === '/hospital/capabilities' ||
    location.pathname === '/hospital/marketplace' ||
    location.pathname === '/hospital/filmop-store' ||
    location.pathname === '/hospital/avagard' ||
    location.pathname.startsWith('/hospital/product/') ||
    location.pathname === '/hospital/blueflux' ||
    location.pathname === '/hospital/blueflux/about' ||
    location.pathname === '/marketplace' ||
    location.pathname === '/contact' ||
    location.pathname === '/search' ||
    location.pathname === '/resources/what-are-neuro-patty-sponges' ||
    location.pathname.startsWith('/marketplace/product/') ||
    location.pathname === '/marketplace/green-klean' ||
    location.pathname === '/marketplace/gloves' ||
    location.pathname === '/marketplace/gloves/ansell' ||
    location.pathname === '/hospital/marketplace/gloves' ||
    location.pathname === '/marketplace/furniture' ||
    location.pathname === '/marketplace/healthcare-examination-furniture' ||
    location.pathname === '/marketplace/di-tank-exchange' ||
    location.pathname === '/hospital/marketplace/green-klean' ||
    location.pathname === '/marketplace/green-klean/vacuum-bags-filters' ||
    location.pathname === '/hospital/marketplace/green-klean/vacuum-bags-filters' ||
    location.pathname === '/marketplace/janitorial' ||
    location.pathname.startsWith('/marketplace/janitorial/') ||
    location.pathname === '/hospital/marketplace/janitorial' ||
    location.pathname.startsWith('/hospital/marketplace/janitorial/') ||
    location.pathname.startsWith('/hospital/portal') ||
    location.pathname === '/hospital/register' ||
    location.pathname === '/hospital/self-register' ||
    location.pathname === '/hospital/setup-password' ||
    location.pathname === '/hospital/register-success' ||
    location.pathname === '/hospital/login' ||
    location.pathname === '/hospital-login' ||
    location.pathname.startsWith('/hospital/setup/') ||
    location.pathname === '/maintenance' ||
    location.pathname === '/order-request' ||
    location.pathname === '/quote-cart' ||
    location.pathname.startsWith('/quote/backorder/') ||
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/reset-password' ||
    location.pathname === '/sales-one-pager' ||
    location.pathname === '/executive-letter' ||
    location.pathname === '/how-it-works' ||
    location.pathname === '/how-it-works/backorder-sourcing' ||
    location.pathname === '/backorders/live-inventory' ||
    location.pathname === '/solutions' ||
    location.pathname === '/solutions/heart' ||
    location.pathname === '/solutions/ims-hart' ||
    location.pathname === '/solutions/general-contracting' ||
    location.pathname === '/solutions/water-treatment' ||
    location.pathname === '/solutions/diabetes-management' ||
    location.pathname.startsWith('/solutions/heart/') ||
    location.pathname === '/solutions/rebate-program' ||
    location.pathname === '/rebate-program' ||
    location.pathname === '/hospital/rebates' ||
    location.pathname === '/admin/rebate-program' ||
    location.pathname === '/diabetes-management' ||
    location.pathname === '/diabetes-care' ||
    location.pathname === '/diversity' ||
    location.pathname === '/diversity-inclusion' ||
    location.pathname === '/unsubscribe' ||
    location.pathname === '/preferences' ||
    location.pathname === '/email-preferences' ||
    location.pathname === '/marketplace/medical-devices' ||
    location.pathname.startsWith('/marketplace/medical-devices/') ||
    location.pathname === '/water-purification' ||
    location.pathname === '/dialysis-water' ||
    location.pathname === '/marketplace/american-surgical' ||
    location.pathname === '/demo/american-surgical' ||
    location.pathname === '/distribution/neuro-sponge' ||
    location.pathname === '/microbiology-testing' ||
    location.pathname === '/services/bioburden' ||
    location.pathname === '/logo-lab' ||
    location.pathname === '/resources/aami-st108' ||
    location.pathname === '/resources/aami-tir34' ||
    location.pathname === '/resources/aami-st91' ||
    location.pathname === '/resources/joint-commission-water-management' ||
    location.pathname === '/resources/hemodialysis-water-treatment' ||
    location.pathname === '/dialysis' ||
    location.pathname === '/sterile-processing' ||
    location.pathname.startsWith('/contractor/public/') ||
    location.pathname.startsWith('/supplier/public/') ||
    location.pathname === '/marketplace/surgical' ||
    location.pathname === '/sterilemate/surgical' ||
    location.pathname.startsWith('/sterilemate/') ||
    location.pathname === '/help' ||
    location.pathname === '/welcome' ||
    location.pathname.startsWith('/welcome/') ||
    location.pathname.startsWith('/user-guides') ||
    location.pathname === '/telegram-walkthrough' ||
    location.pathname === '/sales-playbook' ||
    location.pathname === '/supply-chain-playbook' ||
    location.pathname === '/compliance-demo' ||
    location.pathname === '/security-whitepaper' ||
    location.pathname === '/onboarding-playbook' ||
    location.pathname === '/join-network' ||
    location.pathname === '/become-a-provider' ||
    location.pathname === '/contractor/request-access' ||
    location.pathname === '/supplier/request-access' ||
    location.pathname.startsWith('/supplier/') ||
    location.pathname.startsWith('/contractor/') ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/sales/') ||
    location.pathname === '/sales' ||
    location.pathname.startsWith('/dashboard-new') ||
    location.pathname.startsWith('/asset/') ||
    location.pathname === '/network/dashboard' ||
    location.pathname === '/fleet' ||
    location.pathname.startsWith('/track/') ||
    location.pathname.startsWith('/order/') ||
    location.pathname.startsWith('/account/') ||
    location.pathname === '/developer-hub';
  
  // Determine if onboarding should show based on all conditions
  const shouldShowOnboarding = () => {
    if (isNoOnboardingPage || isFullWidthPage) return false;
    if (user) {
      return localStorage.getItem("sterilemate_staff_onboarded") !== "true";
    }
    return localStorage.getItem("sterilemate_hospital_onboarded") !== "true";
  };
  
  const onboardingType = user ? "staff" : "hospital";
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // Update onboarding visibility when route or user changes
  useEffect(() => {
    setShowOnboarding(shouldShowOnboarding());
  }, [location.pathname, user, isNoOnboardingPage, isFullWidthPage]);
  
  // For full-width pages, render without the widget container constraints
  if (isFullWidthPage) {
    return (
      <>
        <Routes>
          {/* PUBLIC PAGES - No authentication required */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/cover-letter/healthwright-technologies" element={<HealthWrightCoverLetterPage />} />
          <Route path="/proposals" element={<ProposalsIndexPage />} />
          <Route path="/proposals/living-waters" element={<LivingWatersProposalPage />} />
          <Route path="/living-waters" element={<LivingWatersLandingPage />} />
          {/* Hidden share link — not in nav. Safe to hand to Living Waters directly. */}
          <Route path="/share/living-waters" element={<LivingWatersLandingPage />} />
          <Route path="/philanthropy" element={<PhilanthropyPage />} />
          <Route path="/register" element={<RegisterDispatcher />} />
          <Route path="/team/join" element={<TeamJoinPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/sales-one-pager" element={<SalesOnePagerPage />} />
          <Route path="/executive-letter" element={<HospitalExecutiveLetterPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/how-it-works/backorder-sourcing" element={<BackorderSourcingPage />} />
          {/* White-theme full-list landing page — every SKU, no preview cap,
              searchable + per-row "Request Quote" that seeds the backorder form. */}
          <Route path="/backorders/live-inventory" element={<BackorderLiveInventoryPage />} />
          <Route path="/solutions" element={<SolutionsOverviewPage />} />
          <Route path="/diversity" element={<DiversityLandingPage />} />
          <Route path="/diversity-inclusion" element={<DiversityLandingPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/preferences" element={<EmailPreferencesPage />} />
          <Route path="/email-preferences" element={<EmailPreferencesPage />} />
          <Route path="/marketplace/medical-devices" element={<MedicalDevicesLandingPage />} />
          <Route path="/marketplace/medical-devices/:shopSlug" element={<ViveHealthShopPage />} />
          <Route path="/complaint-form" element={<ComplaintFormPage />} />
          <Route path="/medical-device-complaint-form" element={<ComplaintFormPage />} />
          <Route path="/solutions/heart" element={<IMSHARTProductPage />} />
          <Route path="/solutions/heart/:slug" element={<SoftwareAppPage />} />
          <Route path="/solutions/ims-hart" element={<Navigate to="/solutions/heart" replace />} />
          <Route path="/solutions/general-contracting" element={<GeneralContractingPage />} />
          <Route path="/solutions/water-treatment" element={<WaterTreatmentPage />} />
          <Route path="/solutions/diabetes-management" element={<DiabetesManagementPage />} />
          <Route path="/solutions/rebate-program" element={<RebateProgramPage />} />
          <Route path="/rebate-program" element={<Navigate to="/solutions/rebate-program" replace />} />
          <Route path="/hospital/rebates" element={<HospitalRebatesPage />} />
          <Route path="/admin/rebate-program" element={<RebateProgramDeskPage />} />
          <Route path="/diabetes-management" element={<Navigate to="/solutions/diabetes-management" replace />} />
          <Route path="/diabetes-care" element={<Navigate to="/solutions/diabetes-management" replace />} />
          {/* Legacy redirects — old water-campaign email links now point to the canonical solutions page */}
          <Route path="/water-purification" element={<Navigate to="/solutions/water-treatment" replace />} />
          <Route path="/dialysis-water" element={<Navigate to="/solutions/water-treatment" replace />} />
          <Route path="/marketplace/american-surgical" element={<AmericanSurgicalPage />} />
          <Route path="/demo/american-surgical" element={<AmericanSurgicalHospitalDemoPage />} />
          <Route path="/distribution/neuro-sponge" element={<NeuroSpongeDistribution />} />
          <Route path="/microbiology-testing" element={<MicrobiologyTestingPage />} />
          <Route path="/services/bioburden" element={<BioburdenServicesPage />} />
          <Route path="/logo-lab" element={<LogoLabPage />} />
          <Route path="/resources/aami-st108" element={<AamiSt108Page />} />
          <Route path="/resources/aami-tir34" element={<AamiTir34Page />} />
          <Route path="/resources/aami-st91" element={<AamiSt91Page />} />
          <Route path="/resources/joint-commission-water-management" element={<JointCommissionWaterPage />} />
          <Route path="/resources/hemodialysis-water-treatment" element={<HemodialysisWaterPage />} />
          <Route path="/dialysis" element={<DialysisCommandCenterPage />} />
          <Route path="/sterile-processing" element={<SterileProcessingCommandCenterPage />} />
          <Route path="/contractor/public/:contractorId" element={<ContractorPublicProfile />} />
          <Route path="/supplier/public/:supplierId" element={<SupplierPublicProfile />} />
          <Route path="/sterilemate/surgical" element={<SurgicalCatalog />} />
          {/* Sol-M authorized distributor category pages */}
          <Route path="/sterilemate/diabetes-care" element={<SolMCategoryPage />} />
          <Route path="/sterilemate/medication-delivery" element={<SolMCategoryPage />} />
          <Route path="/sterilemate/blood-collection" element={<SolMCategoryPage />} />
          <Route path="/sterilemate/wound-care" element={<SolMCategoryPage />} />
          <Route path="/sterilemate/ppe" element={<SolMCategoryPage />} />
          <Route path="/sterilemate/vascular-access" element={<SolMCategoryPage />} />
          <Route path="/sterilemate/incontinence" element={<SolMCategoryPage />} />
          <Route path="/sterilemate/bulk-non-sterile" element={<SolMCategoryPage />} />
          {/* BEAR-ENT ENT catalog — 6 categories */}
          <Route path="/sterilemate/ent-ear" element={<BearEntCategoryPage />} />
          <Route path="/sterilemate/ent-nose" element={<BearEntCategoryPage />} />
          <Route path="/sterilemate/ent-throat" element={<BearEntCategoryPage />} />
          <Route path="/sterilemate/ent-office-set" element={<BearEntCategoryPage />} />
          <Route path="/sterilemate/ent-surgical" element={<BearEntCategoryPage />} />
          <Route path="/sterilemate/ent-general" element={<BearEntCategoryPage />} />
          {/* Legacy URL — keep for previously shared links */}
          <Route path="/marketplace/surgical" element={<Navigate to="/sterilemate/surgical" replace />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/ai-assistant" element={<AIHelpPage />} />
          <Route path="/hospital/register" element={<HealthSystemRegisterPage />} />
          <Route path="/hospital/self-register" element={<HospitalSelfRegisterPage />} />
          <Route path="/hospital/setup-password" element={<HospitalSetupPasswordPage />} />
          <Route path="/hospital/register-success" element={<HospitalRegisterSuccessPage />} />
          <Route path="/hospital/login" element={<HospitalLoginPage />} />
          <Route path="/hospital-login" element={<HospitalLoginPage />} />
          <Route path="/welcome" element={<WelcomeLetterPage />} />
          <Route path="/welcome/supplier" element={<SupplierWelcomeLetterPage />} />
          <Route path="/welcome/contractor" element={<ContractorWelcomeLetterPage />} />
          <Route path="/user-guides" element={<UserGuidesPage />} />
          <Route path="/user-guides/:role" element={<UserGuidesPage />} />
          <Route path="/telegram-walkthrough" element={<TelegramWalkthroughPage />} />
          <Route path="/sales-playbook" element={<SalesPlaybookPage />} />
          <Route path="/supply-chain-playbook" element={<SupplyChainPlaybook />} />
          <Route path="/compliance-demo" element={<ComplianceDemoSuite />} />
          <Route path="/security-whitepaper" element={<SecurityWhitepaper />} />
          <Route path="/onboarding-playbook" element={<OnboardingPlaybookPage />} />
          <Route path="/developer-hub" element={<DeveloperHubPage />} />
          <Route path="/downloads" element={<DownloadsPage />} />
          <Route path="/account/profile" element={<AccountProfilePage />} />
          <Route path="/account/settings" element={<AccountSettingsPage />} />
          <Route path="/st108" element={<AAMIST108Page />} />
          <Route path="/st108/guide" element={<ST108GuidePage />} />
          <Route path="/aami-st108" element={<AAMIST108Page />} />
          <Route path="/hospital/capabilities" element={<HospitalCapabilitiesPage />} />
          <Route path="/hospital/marketplace" element={<MarketplacePage />} />
          <Route path="/hospital/product/avagard" element={<AvagardLandingPage />} />
          <Route path="/hospital/avagard" element={<AvagardLandingPage />} />
          <Route path="/hospital/blueflux" element={<BlueFluxLandingPage />} />
          <Route path="/hospital/blueflux/about" element={<BlueFluxAboutPage />} />
          <Route path="/hospital/product/:itemNumber" element={<ProductDetailPage />} />
          <Route path="/marketplace/product/:slug" element={<MarketplaceProductPage />} />
          <Route path="/hospital/filmop-store" element={<FilmopStorePage />} />
          
          {/* ENTERPRISE BIDDING & VENDOR SHOWCASE */}
          <Route path="/quickbid" element={<QuickBidPage />} />
          <Route path="/vendor-showcase" element={<VendorShowcasePage />} />
          
          {/* JOIN NETWORK - Landing page for contractor/supplier recruitment emails */}
          <Route path="/join-network" element={<JoinNetworkPage />} />
          <Route path="/become-a-provider" element={<JoinNetworkPage />} />
          <Route path="/contractor/request-access" element={<ContractorRequestAccessPage />} />
          <Route path="/supplier/request-access" element={<SupplierRequestAccessPage />} />
          
          {/* FLEET TELEMETRY - Live Courier Tracking */}
          <Route path="/fleet" element={user ? <FleetDashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/track/:linkId" element={<HospitalTrackingPage />} />
          
          {/* PORTAL LOGIN PAGES - Public but require their own auth */}
          <Route path="/contractor/login" element={<ContractorLoginPage />} />
          <Route path="/contractor/register" element={<ContractorRegisterPage />} />
          <Route path="/supplier/register" element={<Navigate to="/register?type=supplier" replace />} />
          <Route path="/contractor/setup/:token" element={<ContractorSetupPage />} />
          <Route path="/contractor/quote/:token" element={<ContractorQuotePage />} />
          <Route path="/contractor/agreement/:agreementId" element={<ContractorAgreementPage />} />
          <Route path="/contractor/compliance/:contractorId" element={<ContractorCompliancePage />} />
          <Route path="/hospital/setup/:token" element={<HospitalSetupPage />} />
          <Route path="/hospital/approve-quote/:token" element={<HospitalQuoteApprovalPage />} />
          <Route path="/supplier/login" element={<SupplierLoginPage />} />
          <Route path="/supplier/setup/:token" element={<SupplierSetupPage />} />
          
          {/* ASSET LOOKUP - Public QR code landing page */}
          <Route path="/asset/:assetId" element={<AssetLookupPage />} />
          <Route path="/supplier/quote/:token" element={<SupplierQuotePortal />} />
          <Route path="/contractor/maintenance-quote/:requestId" element={<ContractorMaintenanceQuotePortal />} />
          <Route path="/contractor/job/:token" element={<ContractorJobPortal />} />
          
          {/* PUBLIC PHILANTHROPY PAGE - No login required */}
          <Route path="/admin/philanthropy" element={<PhilanthropyPage />} />
          
          {/* PROTECTED PAGES - Require admin login */}
          <Route path="/admin-old/*" element={gate(<AdminDashboard />)} />
          <Route path="/admin/*" element={gate(<AdminDashboardV2 />)} />
          <Route path="/sales/dashboard" element={gate(<SalesRepDashboard />)} />
          <Route
            path="/contractor/portal"
            element={
              <ProtectedRoute allowedRoles={["trade_contractor", "super_admin", "project_manager"]}>
                <ContractorPortalPage />
              </ProtectedRoute>
            }
          />
          <Route path="/dashboard" element={user ? <AskIsaacDashboard /> : <Navigate to="/login" replace />} />
          <Route path="/investor" element={<InvestorPortalPage />} />
          <Route path="/investor/share" element={<InvestorSharePage />} />
          <Route path="/plan" element={<InvestorSharePage />} />
          <Route path="/investor/frost-bank" element={<InvestorPortalPage bypassGate forceScenario="ronan" />} />
          <Route path="/investor/frost-bank/letter" element={<FrostBankCoverLetterPage />} />
          <Route path="/investor/one-pager" element={<InvestorOnePagerPage />} />
          <Route path="/investor/financials" element={<FinancialProjectionsPage />} />
          <Route path="/hospital-presentation" element={<HospitalPresentationPage />} />
          <Route path="/pitch-deck" element={user ? <PitchDeckPage /> : <Navigate to="/login" replace />} />
          <Route path="/demo/evs" element={<EVSDemoPage />} />
          <Route path="/demo/water-purification" element={<WaterPurificationDemoPage />} />
          <Route path="/demo/port-of-houston" element={<PortOfHoustonDemo />} />
          <Route path="/demos" element={<DemoGalleryPage />} />
          
          {/* PROTECTED SERVICE PAGES - Require login */}
          <Route path="/maintenance" element={user ? <MaintenancePage /> : <Navigate to="/login" replace />} />
          <Route path="/order-request" element={user ? <OrderRequestPage /> : <Navigate to="/login" replace />} />
          <Route path="/quote/backorder/:backorderId" element={<PublicBackorderQuotePage />} />
          <Route path="/work-orders/sign-off/:token" element={<WorkOrderSignOffPage />} />
          <Route path="/incidents/:token" element={<IncidentDetailPage />} />
          <Route path="/hospital/backorders" element={<HospitalBackorderPage />} />
          <Route path="/hospital/dashboard" element={user ? <HospitalDashboard /> : <Navigate to="/login" replace />} />
          {/* LEGACY EMAIL REDIRECT — old backorder campaign links pointed to /quote-cart */}
          <Route path="/quote-cart" element={<QuoteCartLegacyRedirect />} />
          <Route path="/order/:token" element={<CustomerOrderPortal />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/marketplace/gloves" element={<GlovesPage />} />
          <Route path="/marketplace/gloves/ansell" element={<AnsellGlovesPage />} />
          <Route path="/marketplace/furniture" element={<FurniturePage />} />
          <Route path="/marketplace/healthcare-examination-furniture" element={<FurniturePage />} />
          <Route path="/hospital/marketplace/gloves" element={<GlovesPage />} />
          <Route path="/marketplace/green-klean" element={<GreenKleenPage />} />
          <Route path="/marketplace/di-tank-exchange" element={<DITankExchangeCatalogPage />} />
          <Route path="/hospital/marketplace/green-klean" element={<GreenKleenPage />} />
          <Route path="/marketplace/green-klean/vacuum-bags-filters" element={<GreenKleenVacuumBagsPage />} />
          <Route path="/hospital/marketplace/green-klean/vacuum-bags-filters" element={<GreenKleenVacuumBagsPage />} />
          <Route path="/marketplace/janitorial" element={<JanitorialCatalogPage basePath="/marketplace/janitorial" />} />
          <Route path="/marketplace/janitorial/:categorySlug" element={<JanitorialCategoryPage basePath="/marketplace/janitorial" />} />
          <Route path="/marketplace/janitorial/:categorySlug/:sku" element={<JanitorialProductPage basePath="/marketplace/janitorial" />} />
          <Route path="/hospital/marketplace/janitorial" element={<JanitorialCatalogPage basePath="/hospital/marketplace/janitorial" />} />
          <Route path="/hospital/marketplace/janitorial/:categorySlug" element={<JanitorialCategoryPage basePath="/hospital/marketplace/janitorial" />} />
          <Route path="/hospital/marketplace/janitorial/:categorySlug/:sku" element={<JanitorialProductPage basePath="/hospital/marketplace/janitorial" />} />
          
          {/* PROTECTED PORTAL DASHBOARDS - Require portal-specific auth */}
          <Route path="/contractor/dashboard" element={<ContractorDashboardV2 />} />
          <Route path="/contractor/dashboard-old" element={<ContractorDashboardPage />} />
          <Route path="/contractor/profile" element={<ContractorProfilePage />} />
          <Route path="/contractor/recruitment" element={<ContractorRecruitmentPageV2 />} />
          <Route path="/contractor/recruitment-old" element={<ContractorRecruitmentPage />} />
          <Route path="/supplier/recruitment" element={<SupplierRecruitmentPage />} />
          <Route path="/join-network" element={<JoinNetworkPage />} />
          <Route path="/become-a-provider" element={<JoinNetworkPage />} />
          <Route path="/supplier/dashboard" element={<SupplierDashboardV2 />} />
          <Route path="/supplier/dashboard-old" element={<SupplierDashboard />} />
          <Route path="/hospital/capabilities" element={<HospitalCapabilitiesPage />} />
          <Route path="/hospital/marketplace" element={<MarketplacePage />} />
          <Route path="/hospital/filmop-store" element={<FilmopStorePage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/hospital/portal/*" element={<HospitalDashboardPortal />} />
          <Route path="/network/dashboard" element={<NetworkDashboardPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/search" element={<SearchResultsPage />} />
          <Route path="/resources/what-are-neuro-patty-sponges" element={<WhatAreNeuroPattiesPage />} />
          <Route path="/sterile-mate" element={<Navigate to="/" replace />} />
          {/* Legacy sterilemate.com WordPress URLs -> 301-equivalent redirects (see src/data/wordpressRedirects.js) */}
          <Route path="*" element={<WordPressRedirect />} />
        </Routes>
        {/* Global Sitemap Footer - shown on public-facing full-width pages */}
        {(location.pathname === '/' || 
          location.pathname === '/about' ||
          location.pathname === '/how-it-works' ||
    location.pathname === '/how-it-works/backorder-sourcing' ||
          location.pathname === '/help' ||
          location.pathname === '/login' ||
          location.pathname === '/st108' ||
          location.pathname === '/st108/guide' ||
          location.pathname === '/aami-st108' ||
          location.pathname === '/sales-one-pager' ||
          location.pathname === '/executive-letter' ||
          location.pathname.startsWith('/welcome') ||
          location.pathname.startsWith('/user-guides') ||
          location.pathname === '/telegram-walkthrough' ||
          location.pathname === '/hospital/register' ||
          location.pathname === '/hospital/login' ||
          location.pathname === '/contractor/login' ||
          location.pathname === '/contractor/register' ||
          location.pathname === '/supplier/login' ||
          location.pathname === '/investor' ||
          location.pathname === '/pitch-deck' ||
          location.pathname === '/hospital-presentation' ||
          location.pathname === '/demo/evs' ||
          location.pathname === '/demo/water-purification' ||
          location.pathname === '/demos'
        ) && <Footer />}
        <Toaster 
          position="top-center" 
          toastOptions={{
            style: {
              background: 'white',
              border: '1px solid #e2e8f0',
              color: '#1e293b',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            },
          }}
        />
        {/* Isaac AI Chat - Floating bubble (hidden on Hospital staff dashboard) */}
        {!hideIsaacChat && <IsaacChat />}
      </>
    );
  }
  
  return (
    <div className="widget-container flex flex-col min-h-screen pb-16 sm:pb-0">
      {/* Onboarding Modal — never show on public token-gated pages */}
      {showOnboarding && !location.pathname.startsWith('/work-orders/sign-off') && !location.pathname.startsWith('/quote/backorder') && !location.pathname.startsWith('/bid/') && !location.pathname.startsWith('/permit/') && !location.pathname.startsWith('/incidents/') && (
        <OnboardingModal 
          isStaff={onboardingType === "staff"} 
          onComplete={() => setShowOnboarding(false)} 
        />
      )}
      
      <WidgetHeader />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/microbiology-testing" element={<MicrobiologyTestingPage />} />
          <Route path="/services/bioburden" element={<BioburdenServicesPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/work-orders/sign-off/:token" element={<WorkOrderSignOffPage />} />
          <Route path="/incidents/:token" element={<IncidentDetailPage />} />
          <Route 
            path="/dashboard-new/*" 
            element={
              <ProtectedRoute allowedRoles={["super_admin", "hub_admin"]}>
                <AdminDashboardV2 />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin-v2/*" 
            element={
              <ProtectedRoute allowedRoles={["super_admin", "hub_admin"]}>
                <AdminDashboardV2 />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/*" 
            element={
              <ProtectedRoute allowedRoles={["super_admin", "hub_admin", "project_manager"]}>
                <AdminDashboardV2 />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin-old/*" 
            element={
              <ProtectedRoute allowedRoles={["super_admin", "hub_admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/worker/*" 
            element={
              <ProtectedRoute allowedRoles={["maintenance_worker"]}>
                <WorkerDashboard />
              </ProtectedRoute>
            } 
          />
          <Route path="/complaint-form" element={<ComplaintFormPage />} />
          <Route path="/medical-device-complaint-form" element={<ComplaintFormPage />} />
          {/* Subcontractor RFQ response — no login, token in the link */}
          <Route path="/bid/:token" element={<SubBidPage />} />
          {/* ICRA permit door sign QR target — no login */}
          <Route path="/permit/:token" element={<PermitSignPage />} />
          {/* Public shareable investor package — no auth required */}
          <Route path="/investor/share" element={<InvestorSharePage />} />
          <Route path="/plan" element={<InvestorSharePage />} />
          <Route path="/investor/frost-bank" element={<InvestorPortalPage bypassGate forceScenario="ronan" />} />
          <Route path="/investor/financials" element={<FinancialProjectionsPage />} />
          {/* Legacy sterilemate.com WordPress URLs -> 301-equivalent redirects (see src/data/wordpressRedirects.js) */}
          <Route path="*" element={<WordPressRedirect />} />
        </Routes>
      </main>
      <WidgetFooter />
      {/* Main Site Footer - shown on non-dashboard pages */}
      {(location.pathname === '/' || 
        location.pathname === '/demo' ||
        location.pathname === '/how-it-works' ||
    location.pathname === '/how-it-works/backorder-sourcing' ||
        location.pathname === '/help' ||
        location.pathname.startsWith('/user-guides') ||
        location.pathname === '/telegram-walkthrough' ||
        location.pathname.startsWith('/welcome') ||
        location.pathname === '/st108' ||
        location.pathname === '/sales-one-pager' ||
        location.pathname === '/investor' ||
        location.pathname === '/pitch-deck' ||
        location.pathname === '/hospital-presentation'
      ) && <Footer />}
      <BottomNavigation />
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: 'white',
            border: '1px solid #e2e8f0',
            color: '#1e293b',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          },
        }}
      />
      {/* Isaac AI Chat - Floating bubble (hidden on Hospital staff dashboard) */}
      {!hideIsaacChat && <IsaacChat />}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <QuoteCartProvider>
            <AskIsaacMovedBanner />
            <AppContent />
            <QuoteCartDialog />
          </QuoteCartProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
